import { rectHeight, rectWidth } from 'tabview/visualizations/misc/geometry';
import { nicenum } from 'tabview/visualizations/misc/math';
import { RenderParameters } from './types';
import { BlocksGraph, BlocksNode } from '../types';
import { isGroup, isNodeOpen } from '../util';
import { getColPositions, getRowPositions } from '../placeandroute/util';

function* visibleNodes(node: BlocksNode): Generator<BlocksNode, void, unknown> {
  if (isGroup(node) && isNodeOpen(node)) {
    for (const child of node.children) {
      yield* visibleNodes(child);
    }
  }

  yield node;
}

export const renderDebugGrid = (
  graph: BlocksGraph,
  params: RenderParameters
) => {
  if (params.isReadonly) {
    return;
  }

  type LineExtent = { count: number; first: number; last: number };

  const colExtents = new Map<number, LineExtent>();
  const colPositions = getColPositions(graph.root);
  const rowExtents = new Map<number, LineExtent>();
  const rowPositions = getRowPositions(graph.root);

  const addToExtent = (
    extents: Map<number, LineExtent>,
    line: number,
    first: number,
    last: number
  ) => {
    const extent = extents.get(line);

    if (!extent) {
      extents.set(line, { count: 1, first, last });
      return;
    }

    extent.count += 1;
    extent.first = Math.min(extent.first, first);
    extent.last = Math.max(extent.last, last);
  };

  for (const node of visibleNodes(graph.root)) {
    if (node.parent && node.layoutData) {
      const bgnCol = node.layoutData.col;
      const endCol = node.layoutData.col + node.layoutData.colSpan;
      const bgnRow = node.layoutData.row;
      const endRow = node.layoutData.row + node.layoutData.rowSpan;

      addToExtent(colExtents, bgnCol, bgnRow, endRow);
      addToExtent(colExtents, endCol, bgnRow, endRow);

      addToExtent(rowExtents, bgnRow, bgnCol, endCol);
      addToExtent(rowExtents, endRow, bgnCol, endCol);
    }
  }

  params.ctx.beginPath();

  for (const [col, { count, first, last }] of colExtents) {
    if (count >= 2) {
      const a = params.projection.projectPointSnapped([
        colPositions[col],
        rowPositions[first],
      ]);
      const b = params.projection.projectPointSnapped([
        colPositions[col],
        rowPositions[last],
      ]);

      params.ctx.moveTo(...a);
      params.ctx.lineTo(...b);
    }
  }

  for (const [row, { count, first, last }] of rowExtents) {
    if (count >= 2) {
      const a = params.projection.projectPointSnapped([
        colPositions[first],
        rowPositions[row],
      ]);
      const b = params.projection.projectPointSnapped([
        colPositions[last],
        rowPositions[row],
      ]);

      params.ctx.moveTo(...a);
      params.ctx.lineTo(...b);
    }
  }
  params.ctx.lineWidth = 1;
  params.ctx.strokeStyle = '#00000020';
  params.ctx.stroke();
};

/* this is the toplevel scenegraph editing grid */
export const renderGrid = (_graph: BlocksGraph, params: RenderParameters) => {
  if (params.isReadonly) {
    return;
  }

  const projection = params.projection;
  const { window } = projection;
  const sep = 30;

  const vp = params.projection.projectBounds(window);
  const targetTicks = [
    Math.ceil(rectWidth(vp) / sep),
    Math.ceil(rectHeight(vp) / sep),
  ];
  const range = [
    nicenum(rectWidth(window), false),
    nicenum(rectHeight(window), false),
  ];
  const step = [
    nicenum(range[0] / (targetTicks[0] - 1), true),
    nicenum(range[1] / (targetTicks[1] - 1), true),
  ];
  const uv = [
    Math.ceil(window[0] / step[0]),
    Math.ceil(window[1] / step[1]),
    Math.floor(window[2] / step[0]),
    Math.floor(window[3] / step[1]),
  ];

  params.ctx.beginPath();

  for (let u = uv[0]; u <= uv[2]; ++u) {
    for (let v = uv[1]; v <= uv[3]; ++v) {
      const xy = params.projection.projectPoint([step[0] * u, step[1] * v]);
      const sz = 3;

      params.ctx.moveTo(xy[0] - sz, xy[1]);
      params.ctx.lineTo(xy[0] + sz, xy[1]);

      params.ctx.moveTo(xy[0], xy[1] - sz);
      params.ctx.lineTo(xy[0], xy[1] + sz);
    }
  }

  params.ctx.lineWidth = 1;
  params.ctx.strokeStyle = '#00000030';
  params.ctx.stroke();
};
